import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth-store';
import { trpc } from './lib/trpc';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/auth/Login';
import { RegisterPage } from './pages/auth/Register';
import { SignupPage } from './pages/auth/Signup';
import { ForgotPasswordPage } from './pages/auth/ForgotPassword';
import { ResetPasswordPage } from './pages/auth/ResetPassword';
import { VerifyMfaPage } from './pages/auth/VerifyMfa';
import { AcceptInvitePage } from './pages/auth/AcceptInvite';
import { DashboardPage } from './pages/dashboard';
import { ErpConnectionsPage } from './pages/erp';
import { QuickBooksCallback } from './pages/erp/quickbooks/Callback';
import { ReportsPage } from './pages/reports';
import { SettingsLayout } from './pages/settings';
import { AccountPage } from './pages/settings/Account';
import { UsersPage } from './pages/settings/Users';
import { OrganizationPage } from './pages/settings/Organization';
import { BillingPage } from './pages/settings/Billing';
import { ReportDesignerPage } from './pages/reports/designer';
import { InstallAddinPage } from './pages/install-addin';
import { EulaPage } from './pages/legal/Eula';
import { PrivacyPage } from './pages/legal/Privacy';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const { isAuthenticated } = useAuthStore();
  const utils = trpc.useUtils();
  const hasTriggeredBillingSync = useRef(false);
  const refreshFromStripeMutation = trpc.subscription.refreshFromStripe.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.tenant.getCurrent.invalidate(),
        utils.tenant.getUsage.invalidate(),
        utils.subscription.getInvoices.invalidate(),
      ]);
    },
  });

  useEffect(() => {
    if (!isAuthenticated) {
      hasTriggeredBillingSync.current = false;
      return;
    }

    if (hasTriggeredBillingSync.current || refreshFromStripeMutation.isPending) {
      return;
    }

    hasTriggeredBillingSync.current = true;
    refreshFromStripeMutation.mutate();
  }, [isAuthenticated, refreshFromStripeMutation]);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-mfa"
        element={
          <PublicRoute>
            <VerifyMfaPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invite"
        element={<AcceptInvitePage />}
      />
      <Route path="/legal/eula" element={<EulaPage />} />
      <Route path="/legal/eula/index.html" element={<EulaPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/privacy/index.html" element={<PrivacyPage />} />

      {/* Report Designer (full-screen, outside Layout) */}
      <Route
        path="/reports/designer"
        element={
          <ProtectedRoute>
            <ReportDesignerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/designer/:templateId"
        element={
          <ProtectedRoute>
            <ReportDesignerPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="erp" element={<ErpConnectionsPage />} />
        <Route path="erp/quickbooks/callback" element={<QuickBooksCallback />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="budgets" element={<Navigate to="/reports" replace />} />
        <Route path="install-addin" element={<InstallAddinPage />} />

        {/* Settings routes */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="/settings/account" replace />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="organization" element={<OrganizationPage />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
